// Questionnaire Step-by-Step Controller
// This controller handles the new step-by-step questionnaire flow where questions appear one at a time

export default class QuestionnaireStepperController {
  constructor() {
    this.currentQuestionIndex = 0;
    this.questions = [];
    this.form = null;
    this.originalSubmitButton = null;
    this.navigationContainer = null;
    this.topNavigationContainer = null;
    this.bottomNavigationContainer = null;
    this.questionContainer = null;
  }

  initialize() {
    // Find the form
    this.form = document.querySelector('.onboarding-form')?.closest('form');
    if (!this.form) {
      console.warn('Questionnaire form not found');
      return false;
    }

    // Check if already initialized
    if (this.form.dataset.stepperInitialized === 'true') {
      console.warn('Questionnaire stepper already initialized');
      return false;
    }

    // Mark as initialized
    this.form.dataset.stepperInitialized = 'true';

    // Find all question divs, but exclude address history questions
    // Address history form needs to show all questions at once for multiple address entries
    const allQuestionElements = document.querySelectorAll('.question');
    this.allQuestions = Array.from(allQuestionElements).filter(question => {
      // Exclude questions that are part of address history form
      // Check if question is inside address-cards, new-address, or address-related containers
      const parent = question.parentElement;
      const isAddressHistory =
        // Check if question is inside address-cards container
        question.closest('.address-cards') !== null ||
        // Check if question is inside new-address container
        question.closest('.new-address') !== null ||
        // Check if question is inside address-data container
        question.closest('.address-data') !== null ||
        // Check if question is inside a container with address-related classes
        (parent && (
          parent.classList.contains('address-cards') ||
          parent.classList.contains('new-address') ||
          parent.classList.contains('address-data') ||
          parent.classList.contains('address-card')
        )) ||
        // Check if question text indicates it's address history related
        (question.textContent && (
          question.textContent.toLowerCase().includes('address history') ||
          question.textContent.toLowerCase().includes('co-applicant address history') ||
          question.textContent.toLowerCase().includes('same address history')
        ));

      return !isAddressHistory;
    });

    if (this.allQuestions.length === 0) {
      console.warn('No questions found in form (after filtering address history)');
      // If no questions after filtering, check if we should still initialize
      // Maybe the form only has address history, in which case we shouldn't use stepper
      if (allQuestionElements.length > 0) {
        console.log('Form contains only address history questions, skipping stepper initialization');
        return false;
      }
      return false;
    }

    // Mark questions that are initially hidden (by Rails or jQuery)
    this.markInitiallyHiddenQuestions();

    // Find and hide the original submit button
    this.originalSubmitButton = this.form.querySelector('input[type="submit"], button[type="submit"]');
    if (this.originalSubmitButton) {
      this.originalSubmitButton.style.display = 'none';
    }

    // Create wrapper for questions
    this.createQuestionWrapper();

    // Hide all questions initially
    this.hideAllQuestions();

    // Create navigation UI
    this.createNavigationUI();

    // Setup mutation observer to watch for dynamic question visibility changes
    this.setupVisibilityObserver();

    // Show first visible question
    this.showQuestion(0);

    // Remove loading class to reveal questions
    if (this.questionContainer) {
      this.questionContainer.classList.remove('question-stepper-loading');
    }

    // Add form submit handler
    this.setupFormHandlers();

    // Add change listeners to all inputs to refresh visible questions
    this.setupInputChangeListeners();

    return true;
  }

  createQuestionWrapper() {
    // Wrap all questions in a container for better control
    if (this.allQuestions.length > 0) {
      const firstQuestion = this.allQuestions[0];
      const parent = firstQuestion.parentElement;

      this.questionContainer = document.createElement('div');
      this.questionContainer.className = 'question-stepper-container question-stepper-loading';
      this.questionContainer.style.minHeight = '400px';

      // Insert wrapper before first question
      parent.insertBefore(this.questionContainer, firstQuestion);

      // Move all questions into wrapper
      this.allQuestions.forEach(question => {
        this.questionContainer.appendChild(question);
      });
    }
  }

  markInitiallyHiddenQuestions() {
    // This is no longer needed since getVisibleQuestions() checks in real-time
    // Just log initial state for debugging
    const visible = this.allQuestions.filter(q => {
      return !q.classList.contains('hidden') && window.getComputedStyle(q).display !== 'none';
    });
    console.log(`Initial: ${visible.length} questions visible out of ${this.allQuestions.length}`);
  }

  isQuestionVisibleToApp(question) {
    // Check if question is hidden by the APP (not by stepper)

    // 1. If has 'hidden' class, it's hidden by Rails or jQuery
    if (question.classList.contains('hidden')) {
      return false;
    }

    // 2. If has stepper-hidden class, ignore it (that's us)
    if (question.classList.contains('stepper-hidden')) {
      // Check if it ALSO has inline display:none from jQuery
      const inlineDisplay = question.style.display;
      if (inlineDisplay === 'none' && question.dataset.jqueryHidden === 'true') {
        return false;
      }
      // Only hidden by stepper, so it's potentially visible to app
      return true;
    }

    // 3. Check if jQuery has set display:none (inline style)
    const inlineDisplay = question.style.display;
    if (inlineDisplay === 'none') {
      // jQuery or Rails has hidden it
      return false;
    }

    // Question appears to be visible to the app
    return true;
  }

  getVisibleQuestions() {
    // Get questions that SHOULD be visible according to app logic
    // NOTE: We do NOT include dynamically added questions from containers here
    // They are shown together with their parent question, not as separate steps
    return this.allQuestions.filter(q => this.isQuestionVisibleToApp(q));
  }

  getContainerQuestionsForQuestion(question) {
    // Get dynamically added questions from containers that belong to this question
    // These should be shown together with the parent question, not as separate steps
    // NOTE: These containers don't contain .question elements, they're just form content
    // So we return empty array - the containers themselves will be shown/hidden
    return [];
  }

  getActiveContainerForQuestion(question) {
    // Check if this question has an associated container that should be shown
    if (!question) return null;

    const checkedInputs = question.querySelectorAll('input[type="radio"]:checked');
    let activeContainer = null;

    checkedInputs.forEach(input => {
      const value = input.value;
      const name = input.name || '';
      const questionText = question.querySelector('h2')?.textContent || '';

      if (value === 'Yes') {
        if (name.includes('borrow-more-money') || questionText.toLowerCase().includes('borrow more money')) {
          activeContainer = document.getElementById('extra-money-container');
        } else if (name.includes('additional-income') || questionText.toLowerCase().includes('additional income')) {
          activeContainer = document.getElementById('extra-income-container');
        }
      }
    });

    return activeContainer;
  }

  setupVisibilityObserver() {
    // Watch for class and style changes on questions (for conditional show/hide logic)
    const observer = new MutationObserver((mutations) => {
      let needsRefresh = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const question = mutation.target;

          if (mutation.attributeName === 'class') {
            // Mark question as conditionally hidden/visible based on 'hidden' class
            if (question.classList.contains('hidden')) {
              question.dataset.conditionallyHidden = 'true';
              needsRefresh = true;
            } else {
              delete question.dataset.conditionallyHidden;
              needsRefresh = true;
            }
          }

          if (mutation.attributeName === 'style') {
            // jQuery's .hide()/.show() changes the style attribute
            // Check if jQuery is showing/hiding this question
            const computedDisplay = window.getComputedStyle(question).display;
            const isActive = question.classList.contains('question-active');

            // If jQuery set display:none and it's not the active question, mark as conditionally hidden
            if (computedDisplay === 'none' && !isActive && question.dataset.stepperHidden !== 'true') {
              question.dataset.conditionallyHidden = 'true';
              needsRefresh = true;
            } else if (computedDisplay !== 'none' && !isActive && !question.classList.contains('hidden')) {
              // jQuery is trying to show a question, but it's not the current one
              // Re-hide it immediately
              question.style.display = 'none';
              question.dataset.stepperHidden = 'true';
            }
          }
        }
      });

      if (needsRefresh) {
        // Update navigation to reflect new visible question count
        this.updateNavigation();
      }
    });

    // Observe all questions for class and style changes
    this.allQuestions.forEach(question => {
      observer.observe(question, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    });

    this.visibilityObserver = observer;
  }

  setupInputChangeListeners() {
    // Listen to all input changes to refresh visible questions list
    this.allQuestions.forEach(question => {
      const inputs = question.querySelectorAll('input, select, textarea');
      inputs.forEach(input => {
        // Listen in normal phase (after the app's handlers run)
        input.addEventListener('change', () => {
          // Auto-continue for radio buttons - wait for conditional logic to complete
          if (input.type === 'radio' && input.checked) {
            // Wait for conditional logic functions to run and refreshQuestionnaireStepper to complete
            // Reduced delay for faster response time
            setTimeout(() => {
              this.handleRadioAutoContinue();
            }, 300); // Reduced from 550ms to 200ms for faster UX
            return; // Skip the normal refresh for radio buttons
          }

          // Wait for jQuery animations to complete (minimal delay)
          setTimeout(() => {
            this.refreshVisibilityState();
            this.enforceStepperVisibility();
            this.updateNavigation();
          }, 100); // Reduced from 250ms to 100ms for faster UX
        });
      });
    });
  }

  handleRadioAutoContinue() {
    // Check if this is an extra-money or extra-income question
    // Don't auto-continue for these as they need to show additional form content
    const currentQuestion = this.getVisibleQuestions()[this.currentQuestionIndex];
    if (currentQuestion) {
      const questionInputs = currentQuestion.querySelectorAll('input[type="radio"]');
      let isExtraQuestion = false;

      questionInputs.forEach(input => {
        const name = input.name || '';
        if (name.includes('borrow-more-money') || name.includes('additional-income')) {
          isExtraQuestion = true;
        }
      });

      // For extra questions, don't auto-continue
      // User needs to fill in the form content first
      if (isExtraQuestion) {
        // Just update navigation and refresh visibility, don't continue
        this.refreshVisibilityState();
        this.enforceStepperVisibility();
        this.updateNavigation();
        return;
      }
    }

    // Refresh visibility state one more time to ensure we have the latest state
    this.refreshVisibilityState();
    this.enforceStepperVisibility();

    const visibleQuestions = this.getVisibleQuestions();
    const isLastQuestion = this.currentQuestionIndex === visibleQuestions.length - 1;

    // If it's the final question, just update navigation to show submit button
    if (isLastQuestion) {
      this.updateNavigation();
      return;
    }

    // Validate current question before auto-continuing
    if (this.validateCurrentQuestion()) {
      // Continue to next question after refresh is complete
      this.handleContinue();
    }
  }

  enforceStepperVisibility() {
    // After conditional logic runs, ensure only current question is visible

    // Get fresh list of visible questions (this checks in real-time)
    const visibleQuestions = this.getVisibleQuestions();
    const currentQuestion = visibleQuestions[this.currentQuestionIndex];

    // If current question is undefined (maybe it got hidden), go to first visible question
    if (!currentQuestion && visibleQuestions.length > 0) {
      this.currentQuestionIndex = 0;
      this.showQuestion(0);
      return;
    }

    // Hide all questions except the current one using CSS class
    this.allQuestions.forEach((question) => {
      if (question !== currentQuestion) {
        // Mark as hidden by jQuery if it has inline display:none
        if (question.style.display === 'none') {
          question.dataset.jqueryHidden = 'true';
        }

        // Add stepper-hidden class
        question.classList.add('stepper-hidden');
        question.classList.remove('question-active');
        question.dataset.stepperHidden = 'true';
      } else {
        // Show current question
        question.classList.remove('stepper-hidden');
        question.classList.add('question-active');
        delete question.dataset.stepperHidden;
        delete question.dataset.jqueryHidden;

        // Only set display:grid if jQuery hasn't hidden it
        if (!question.classList.contains('hidden')) {
          question.style.display = 'grid';
        }
      }
    });

    // Show the active container for this question (if any)
    // These containers hold form content, not .question elements
    const activeContainer = this.getActiveContainerForQuestion(currentQuestion);
    if (activeContainer) {
      console.log('Active container found:', activeContainer.id, 'Children:', activeContainer.children.length);
      if (activeContainer.children.length > 0) {
        // Container has content (form was appended), show it
        activeContainer.style.display = 'block';
        activeContainer.style.visibility = 'visible';
        activeContainer.classList.remove('stepper-hidden');
        activeContainer.classList.remove('hidden');
        console.log('Container shown:', activeContainer.id);
      } else {
        console.log('Container empty, not showing yet');
      }
    }
  }

  hideAllQuestions() {
    this.allQuestions.forEach(question => {
      // Mark as hidden by stepper (so we can distinguish from conditional hiding)
      question.dataset.stepperHidden = 'true';
      question.classList.add('stepper-hidden');
      question.classList.remove('question-active');

      // Mark if jQuery has also hidden it
      if (question.style.display === 'none' || question.classList.contains('hidden')) {
        question.dataset.jqueryHidden = 'true';
      }
    });

    // Ensure containers are never hidden - they need to be visible for forms to be appended
    const extraMoneyContainer = document.getElementById('extra-money-container');
    const extraIncomeContainer = document.getElementById('extra-income-container');

    if (extraMoneyContainer) {
      extraMoneyContainer.style.display = 'block';
      extraMoneyContainer.style.visibility = 'visible';
      extraMoneyContainer.classList.remove('stepper-hidden');
    }

    if (extraIncomeContainer) {
      extraIncomeContainer.style.display = 'block';
      extraIncomeContainer.style.visibility = 'visible';
      extraIncomeContainer.classList.remove('stepper-hidden');
    }
  }

  showQuestion(index) {
    const visibleQuestions = this.getVisibleQuestions();

    if (index < 0 || index >= visibleQuestions.length) {
      return;
    }

    // Hide ALL questions first (enforce single question visibility)
    this.allQuestions.forEach(q => {
      q.classList.add('stepper-hidden');
      q.dataset.stepperHidden = 'true';
      q.classList.remove('question-active');

      // Mark if jQuery has hidden it
      if (q.style.display === 'none' || q.classList.contains('hidden')) {
        q.dataset.jqueryHidden = 'true';
      }
    });

    // Show new question
    this.currentQuestionIndex = index;
    const question = visibleQuestions[index];

    // Remove stepper hidden flags
    question.classList.remove('stepper-hidden');
    delete question.dataset.stepperHidden;
    delete question.dataset.jqueryHidden;
    question.style.display = 'grid';
    question.classList.add('question-active');

    // Ensure containers for this question are visible (so forms can be appended)
    // Check if this is an extra-money or extra-income question
    const questionText = question.querySelector('h2')?.textContent || '';
    const questionInputs = question.querySelectorAll('input[type="radio"]');

    questionInputs.forEach(input => {
      const name = input.name || '';
      if (name.includes('borrow-more-money') || questionText.toLowerCase().includes('borrow more money')) {
        const container = document.getElementById('extra-money-container');
        if (container) {
          container.style.display = 'block';
          container.style.visibility = 'visible';
          container.classList.remove('stepper-hidden');
          container.classList.remove('hidden');
        }
      } else if (name.includes('additional-income') || questionText.toLowerCase().includes('additional income')) {
        const container = document.getElementById('extra-income-container');
        if (container) {
          container.style.display = 'block';
          container.style.visibility = 'visible';
          container.classList.remove('stepper-hidden');
          container.classList.remove('hidden');
        }
      }
    });

    // Move bottom navigation button to the active question (right after question content)
    // Find the last child of the question (usually the input/options area)
    // or append it at the end
    if (this.bottomNavigationContainer && this.bottomNavigationContainer.parentNode !== question) {
      // Remove from previous location
      if (this.bottomNavigationContainer.parentNode) {
        this.bottomNavigationContainer.parentNode.removeChild(this.bottomNavigationContainer);
      }
      // Append to current question (at the end, after all inputs/options)
      question.appendChild(this.bottomNavigationContainer);
    }

    // Add Typeform-like slide-in animation
    question.style.opacity = '0';
    question.style.transform = 'translateY(30px) scale(0.98)';

    requestAnimationFrame(() => {
      question.style.transition = 'opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      question.style.opacity = '1';
      question.style.transform = 'translateY(0) scale(1)';
    });

    // Update navigation state
    this.updateNavigation();

    // Scroll to top navigation smoothly with some top spacing in one go
    if (this.topNavigationContainer) {
      const navRect = this.topNavigationContainer.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const targetPosition = navRect.top + scrollTop - 20; // 20px offset from top
      window.scrollTo({ top: targetPosition, behavior: 'smooth' });
    }
  }

  createNavigationUI() {
    // Check if navigation already exists
    const existingTopNav = document.querySelector('.question-navigation-top');
    if (existingTopNav) {
      this.topNavigationContainer = existingTopNav;
      this.bottomNavigationContainer = document.querySelector('.question-navigation-bottom');
      return;
    }

    // Add styles once
    if (!document.getElementById('questionnaire-stepper-styles')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'questionnaire-stepper-styles';
      styleSheet.textContent = `
        .question-navigation-top {
          margin-top: 2rem;
          margin-bottom: -1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }

        .question-navigation-bottom {
          margin-top: 2rem;
          margin-bottom: 0;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }

        /* Ensure question container doesn't have fixed height */
        .question-stepper-container {
          min-height: auto !important;
        }

        /* Ensure extra-money and extra-income containers are always visible when their parent question is active */
        /* Containers are siblings of questions, so we need to show them when the question is active */
        .question-active ~ #extra-money-container,
        .question-active ~ #extra-income-container {
          display: block !important;
          visibility: visible !important;
        }

        /* Also handle case where container might be inside the question */
        .question-active #extra-money-container,
        .question-active #extra-income-container {
          display: block !important;
          visibility: visible !important;
        }

        /* Ensure containers are never hidden by stepper class */
        #extra-money-container.stepper-hidden,
        #extra-income-container.stepper-hidden {
          display: block !important;
          visibility: visible !important;
        }

        .question-continue-btn,
        .question-submit-btn {
          width: 100%;
          padding: 0.75rem 2.5rem;
          padding-left: 2.5rem;
          padding-right: 2.5rem;
          border: none;
          border-radius: 0.5rem;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
          box-sizing: border-box;
        }

        .question-continue-btn:hover {
          background-color: #2800A3 !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .question-submit-btn:hover {
          background-color: #059669;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .question-nav-buttons {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .question-nav-btn {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.5rem;
          background-color: #3500D4;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .question-nav-btn:hover:not(:disabled) {
          background-color: #2800A3;
        }

        .question-nav-btn:disabled {
          background-color: #E5E7EB;
          opacity: 1;
          cursor: not-allowed;
        }

        .question-nav-btn svg {
          width: 20px;
          height: 20px;
        }

        .question-nav-btn:not(:disabled) svg path {
          stroke: white;
        }

        .question-nav-btn:disabled svg path {
          stroke: #969696;
        }

        .question-continue-btn:disabled,
        .question-submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .question-continue-btn.loading,
        .question-submit-btn.loading {
          position: relative;
          color: transparent;
          pointer-events: none;
        }

        .question-continue-btn.loading::after,
        .question-submit-btn.loading::after {
          content: "";
          position: absolute;
          width: 20px;
          height: 20px;
          top: 50%;
          left: 50%;
          margin-left: -10px;
          margin-top: -10px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spinner 0.8s linear infinite;
        }

        @keyframes spinner {
          to {
            transform: rotate(360deg);
          }
        }

        .question-progress-desktop {
          display: block;
        }

        .question-progress-mobile {
          display: none;
          flex: 1;
          text-align: center;
          padding: 0 0.5rem;
        }

        @media (max-width: 640px) {
          .question-navigation-top {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }

          .question-progress-desktop {
            display: none;
          }

          .question-progress-mobile {
            display: block;
          }

          .question-navigation-top .question-nav-buttons {
            flex: 1;
            display: flex;
            gap: 0.5rem;
            align-items: center;
            justify-content: space-between;
          }
        }

        .question-active {
          animation: questionSlideIn 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        @keyframes questionSlideIn {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* Stepper visibility control */
        .question-stepper-container .question.stepper-hidden {
          display: none !important;
        }

        /* Hide all questions initially to prevent flash of content */
        .question-stepper-container.question-stepper-loading .question,
        .question-stepper-container .question {
          display: none !important;
        }

        /* Only show active question */
        .question-stepper-container .question.question-active {
          display: grid !important;
          animation: questionSlideIn 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
      `;
      document.head.appendChild(styleSheet);
    }

    // Create TOP navigation (counter + back/next buttons)
    this.topNavigationContainer = document.createElement('div');
    this.topNavigationContainer.className = 'question-navigation-top';
    this.topNavigationContainer.innerHTML = `
      <div class="question-progress-desktop text-xs sm:text-sm text-[#131624] font-FuturaPTHeavy tracking-wide uppercase">
        Question <span id="current-question-number">1</span> of <span id="total-questions">${this.getVisibleQuestions().length}</span>
      </div>

      <div class="question-nav-buttons">
        <button type="button" id="question-back-btn" class="question-nav-btn" aria-label="Previous question">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.33316 10L16.6665 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M7.49972 5.83366C7.49972 5.83366 3.3331 8.90236 3.33309 10.0004C3.33308 11.0984 7.49976 14.167 7.49976 14.167" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div class="question-progress-mobile text-xs text-[#131624] font-FuturaPTHeavy tracking-wide uppercase">
          Question <span id="current-question-number-mobile">1</span> of <span id="total-questions-mobile">${this.getVisibleQuestions().length}</span>
        </div>

        <button type="button" id="question-next-btn" class="question-nav-btn" aria-label="Next question">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.6668 10L3.3335 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12.5003 14.1663C12.5003 14.1663 16.6669 11.0976 16.6669 9.99964C16.6669 8.90165 12.5002 5.83301 12.5002 5.83301" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;

    // Create BOTTOM navigation (continue/submit button)
    this.bottomNavigationContainer = document.createElement('div');
    this.bottomNavigationContainer.className = 'question-navigation-bottom px-10';
    this.bottomNavigationContainer.innerHTML = `
      <button type="button" id="question-continue-btn" class="question-continue-btn whitespace-nowrap bg-[#3500D4] text-white font-FuturaPTHeavy">
        Continue
      </button>

      <button type="button" id="question-submit-btn" class="question-submit-btn whitespace-nowrap bg-[#00D5A3] text-ink font-FuturaPTHeavy" style="display: none;">
        Continue to the next page
      </button>
    `;

    // Insert TOP navigation BEFORE question container
    this.questionContainer.parentElement.insertBefore(
      this.topNavigationContainer,
      this.questionContainer
    );

    // Insert BOTTOM navigation INSIDE question container (will be moved to active question)
    // Initially append to container, will be moved to active question in showQuestion
    this.questionContainer.appendChild(this.bottomNavigationContainer);

    // Add event listeners
    document.getElementById('question-back-btn').addEventListener('click', () => this.goBack());
    document.getElementById('question-next-btn').addEventListener('click', () => this.goNext());
    document.getElementById('question-continue-btn').addEventListener('click', () => this.handleContinue());
    document.getElementById('question-submit-btn').addEventListener('click', () => this.handleSubmit());

    // Store reference for hide/show during submission
    this.navigationContainer = this.topNavigationContainer; // For backward compatibility
  }

  isExperienceAdditionalCommentsQuestion(questionElement) {
    if (!questionElement) return false;
    const heading = questionElement.querySelector('h2');
    const text = (heading && heading.textContent) ? heading.textContent.trim() : '';
    return text.indexOf('Any additional comments') !== -1 || text.indexOf('additional comments or specific requirements') !== -1;
  }

  updateNavigation() {
    const backBtn = document.getElementById('question-back-btn');
    const nextBtn = document.getElementById('question-next-btn');
    const continueBtn = document.getElementById('question-continue-btn');
    const submitBtn = document.getElementById('question-submit-btn');
    const currentNumberSpan = document.getElementById('current-question-number');
    const totalQuestionsSpan = document.getElementById('total-questions');
    const currentNumberSpanMobile = document.getElementById('current-question-number-mobile');
    const totalQuestionsSpanMobile = document.getElementById('total-questions-mobile');

    const visibleQuestions = this.getVisibleQuestions();

    // Update question numbers (desktop and mobile)
    const currentNumber = this.currentQuestionIndex + 1;
    const totalNumber = visibleQuestions.length;

    if (currentNumberSpan) currentNumberSpan.textContent = currentNumber;
    if (totalQuestionsSpan) totalQuestionsSpan.textContent = totalNumber;
    if (currentNumberSpanMobile) currentNumberSpanMobile.textContent = currentNumber;
    if (totalQuestionsSpanMobile) totalQuestionsSpanMobile.textContent = totalNumber;

    // Update back button state
    backBtn.disabled = this.currentQuestionIndex === 0;

    // Update next button state (disable on last question, but keep visible)
    const isLastQuestion = this.currentQuestionIndex >= visibleQuestions.length - 1;
    nextBtn.disabled = isLastQuestion;

    // Show/hide continue/submit buttons based on position
    if (isLastQuestion) {
      continueBtn.style.display = 'none';
      submitBtn.style.display = 'inline-block';
      // Custom label when last question is experience-additional-comments
      const lastQuestion = visibleQuestions[visibleQuestions.length - 1];
      submitBtn.textContent = this.isExperienceAdditionalCommentsQuestion(lastQuestion)
        ? 'GREAT: TAKE ME TO MY PERSONAL MORTGAGE REVIEW'
        : 'Continue to the next page';
      // Keep next button visible but disabled
      nextBtn.style.display = 'flex';
    } else {
      continueBtn.style.display = 'inline-block';
      submitBtn.style.display = 'none';
      nextBtn.style.display = 'flex';
    }
  }

  goBack() {
    // Refresh visibility state before navigating
    this.refreshVisibilityState();

    if (this.currentQuestionIndex > 0) {
      this.showQuestion(this.currentQuestionIndex - 1);
    }
  }

  goNext() {
    // Refresh visibility state before navigating
    this.refreshVisibilityState();

    const visibleQuestions = this.getVisibleQuestions();
    if (this.currentQuestionIndex < visibleQuestions.length - 1) {
      this.showQuestion(this.currentQuestionIndex + 1);
    }
  }

  handleContinue(skipValidation = false) {
    const continueBtn = document.getElementById('question-continue-btn');

    // Prevent multiple clicks
    if (continueBtn && (continueBtn.disabled || continueBtn.classList.contains('loading'))) {
      return;
    }

    // Refresh visibility state before continuing
    this.refreshVisibilityState();

    // Validate current question before continuing (unless skipped, e.g. "I'm Unsure" for ideal completion date)
    if (skipValidation || this.validateCurrentQuestion()) {
      // Show loading state
      if (continueBtn) {
        continueBtn.classList.add('loading');
        continueBtn.disabled = true;
      }

      // Proceed to next question
      this.goNext();

      // Remove loading state after navigation completes (using requestAnimationFrame for smooth transition)
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (continueBtn) {
            continueBtn.classList.remove('loading');
            continueBtn.disabled = false;
          }
        }, 300);
      });
    } else {
      // Validation failed, ensure button is not in loading state
      if (continueBtn) {
        continueBtn.classList.remove('loading');
        continueBtn.disabled = false;
      }
    }
  }

  refreshVisibilityState() {
    // Re-check which questions should be visible according to app logic
    // This is called before navigation to ensure we have the latest state

    // No need to mark anything - getVisibleQuestions() will check in real-time
    // Just log for debugging
    const visible = this.getVisibleQuestions();
    console.log(`Refresh: ${visible.length} questions visible out of ${this.allQuestions.length}`);
  }

  validateCurrentQuestion() {
    const visibleQuestions = this.getVisibleQuestions();
    const currentQuestion = visibleQuestions[this.currentQuestionIndex];

    if (!currentQuestion) {
      return true;
    }

    // Check if current question has an active container (extra-money or extra-income)
    const activeContainer = this.getActiveContainerForQuestion(currentQuestion);

    console.log('Validating question, active container:', activeContainer ? activeContainer.id : 'none',
                'Container children:', activeContainer ? activeContainer.children.length : 0);

    // Find all required inputs in current question
    let requiredInputs = currentQuestion.querySelectorAll('[required], .required input, .required select, .required textarea');

    // Also include required inputs from container if it has content (form was appended)
    // AND "Yes" is selected
    if (activeContainer && activeContainer.children.length > 0) {
      const containerRequiredInputs = activeContainer.querySelectorAll('[required], .required input, .required select, .required textarea');
      // Combine both sets of required inputs
      requiredInputs = Array.from(requiredInputs).concat(Array.from(containerRequiredInputs));
      console.log('Including container validation, total required inputs:', requiredInputs.length);
    } else if (activeContainer) {
      console.log('Container exists but empty, not including in validation');
    }

    // Check if question header has 'required' class
    const questionHeader = currentQuestion.querySelector('h2.required');
    let hasRequiredHeader = questionHeader !== null;

    // If no required inputs but header is marked required, check for any input
    if (hasRequiredHeader && requiredInputs.length === 0) {
      let allInputs = currentQuestion.querySelectorAll('input:not([type="hidden"]), select, textarea');

      // Also check inputs in container if it has content
      if (activeContainer && activeContainer.children.length > 0) {
        const containerInputs = activeContainer.querySelectorAll('input:not([type="hidden"]), select, textarea');
        allInputs = Array.from(allInputs).concat(Array.from(containerInputs));
      }

      let isAnswered = false;
      allInputs.forEach(input => {
        if (input.type === 'radio' || input.type === 'checkbox') {
          if (input.checked) isAnswered = true;
        } else if (input.value && input.value.trim() !== '') {
          isAnswered = true;
        }
      });

      if (!isAnswered) {
        this.showValidationError('Please answer this required question before continuing.');
        return false;
      }
    }

    // Check each required input
    for (let input of requiredInputs) {
      // Determine which container to search in (current question or active container)
      const searchContainer = (activeContainer && activeContainer.contains(input))
        ? activeContainer
        : currentQuestion;

      if (input.type === 'radio') {
        // For radio buttons, check if any in the group is checked
        const name = input.name;
        const anyChecked = searchContainer.querySelector(`input[name="${name}"]:checked`);
        if (!anyChecked) {
          this.showValidationError('Please select an option before continuing.');
          input.focus();
          return false;
        }
      } else if (input.type === 'checkbox') {
        // For checkboxes with required class parent, check if any is checked
        const checkboxGroup = input.closest('.required');
        if (checkboxGroup) {
          const anyChecked = checkboxGroup.querySelector('input[type="checkbox"]:checked');
          if (!anyChecked) {
            this.showValidationError('Please select at least one option before continuing.');
            input.focus();
            return false;
          }
        } else if (!input.checked) {
          this.showValidationError('Please check this required field before continuing.');
          input.focus();
          return false;
        }
      } else {
        // For text inputs, selects, textareas
        if (!input.value || input.value.trim() === '') {
          this.showValidationError('Please fill in these required fields before continuing.');
          input.focus();
          return false;
        }
      }
    }

    return true;
  }

  showValidationError(message) {
    // Remove any existing error messages
    const visibleQuestions = this.getVisibleQuestions();
    const currentQuestion = visibleQuestions[this.currentQuestionIndex];
    if (currentQuestion) {
      // Look for existing error container (wrapper with px-10)
      const existingErrorContainer = currentQuestion.querySelector('.px-10');
      if (existingErrorContainer && existingErrorContainer.querySelector('.question-validation-error')) {
        existingErrorContainer.remove();
      } else {
        // Fallback: look for old error format (without container)
        const existingError = currentQuestion.querySelector('.question-validation-error');
        if (existingError) {
          existingError.remove();
        }
      }
    }

    // Create error message container with padding
    const errorContainer = document.createElement('div');
    errorContainer.className = 'px-10';
    errorContainer.style.cssText = 'margin-top: 1rem;';

    // Create error message box
    const errorDiv = document.createElement('div');
    errorDiv.className = 'question-validation-error';
    errorDiv.style.cssText = `
      background-color: #FEE2E2;
      border: 1px solid #EF4444;
      color: #991B1B;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      font-family: 'Instrument Sans', sans-serif;
      font-size: 0.875rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    `;
    errorDiv.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width: 1.25rem; height: 1.25rem; flex-shrink: 0;">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
      <span class="font-MontserratRegular text-xs sm:text-sm text-[#991B1B]">${message}</span>
    `;

    // Wrap error box in container
    errorContainer.appendChild(errorDiv);

    // Insert error at the top of the question, right after the heading
    // Find the heading container (div with flex mt-4) or the h2 element
    const headingContainer = currentQuestion.querySelector('.flex.mt-4');
    const h2Element = currentQuestion.querySelector('h2');

    if (headingContainer) {
      // Insert after the heading container (which contains h2 and check icon)
      headingContainer.parentNode.insertBefore(errorContainer, headingContainer.nextSibling);
    } else if (h2Element) {
      // Fallback: insert after h2 if heading container not found
      h2Element.parentNode.insertBefore(errorContainer, h2Element.nextSibling);
    } else {
      // Last resort: insert at the beginning of the question
      currentQuestion.insertBefore(errorContainer, currentQuestion.firstChild);
    }

    // Remove error after 5 seconds
    setTimeout(() => {
      errorContainer.style.opacity = '0';
      errorContainer.style.transition = 'opacity 0.3s';
      setTimeout(() => errorContainer.remove(), 300);
    }, 5000);

    // Scroll to error
    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  handleSubmit() {
    const submitBtn = document.getElementById('question-submit-btn');

    // Prevent multiple submissions
    if (submitBtn && (submitBtn.disabled || submitBtn.classList.contains('loading'))) {
      return;
    }

    // Validate all required questions before submitting
    const invalidQuestionIndex = this.findFirstInvalidQuestion();

    if (invalidQuestionIndex !== -1) {
      // Show the invalid question
      this.showQuestion(invalidQuestionIndex);
      this.showValidationError('Please complete this required question before submitting.');
      return;
    }

    // Show loading state on submit button
    if (submitBtn) {
      submitBtn.classList.add('loading');
      submitBtn.disabled = true;
    }

    // Also disable continue button if visible
    const continueBtn = document.getElementById('question-continue-btn');
    if (continueBtn) {
      continueBtn.disabled = true;
    }

    // All questions are valid, submit the form
    // Make all questions visible before submission so form data is captured
    this.allQuestions.forEach(question => {
      // Remove stepper hidden flag but respect conditional hiding
      if (question.dataset.stepperHidden) {
        delete question.dataset.stepperHidden;
        // Only show if not conditionally hidden
        if (!question.classList.contains('hidden')) {
          question.style.display = 'grid';
        }
      }
    });

    // Keep navigation containers visible but disable all buttons
    if (this.topNavigationContainer) {
      const topButtons = this.topNavigationContainer.querySelectorAll('button');
      topButtons.forEach(btn => {
        btn.disabled = true;
      });
    }

    // Keep bottom navigation visible with loading state
    // (button is already in loading state and disabled)

    // Show original submit button temporarily
    if (this.originalSubmitButton) {
      this.originalSubmitButton.style.display = 'block';
    }

    // Allow form submission
    this.form.dataset.stepperAllowSubmit = 'true';

    if (this.originalSubmitButton) {
      this.originalSubmitButton.click();
    } else {
      this.form.requestSubmit();
    }
  }

  findFirstInvalidQuestion() {
    const visibleQuestions = this.getVisibleQuestions();

    for (let i = 0; i < visibleQuestions.length; i++) {
      const question = visibleQuestions[i];

      // Check for required header
      const questionHeader = question.querySelector('h2.required');
      const hasRequiredHeader = questionHeader !== null;

      if (hasRequiredHeader) {
        const allInputs = question.querySelectorAll('input:not([type="hidden"]), select, textarea');
        let isAnswered = false;

        allInputs.forEach(input => {
          if (input.type === 'radio' || input.type === 'checkbox') {
            if (input.checked) isAnswered = true;
          } else if (input.value && input.value.trim() !== '') {
            isAnswered = true;
          }
        });

        if (!isAnswered) {
          return i;
        }
      }

      // Check required inputs
      const requiredInputs = question.querySelectorAll('[required]');
      for (let input of requiredInputs) {
        if (input.type === 'radio') {
          const name = input.name;
          const anyChecked = question.querySelector(`input[name="${name}"]:checked`);
          if (!anyChecked) {
            return i;
          }
        } else if (input.type === 'checkbox') {
          if (!input.checked) {
            return i;
          }
        } else {
          if (!input.value || input.value.trim() === '') {
            return i;
          }
        }
      }
    }

    return -1; // All questions are valid
  }

  setupFormHandlers() {
    // "I'm Unsure" for experience-ideal-completion-date: move to next question without requiring the date
    this.form.addEventListener('click', (e) => {
      const unsureBtn = e.target.id === 'experience-ideal-completion-date-im-unsure' ? e.target : e.target.closest('#experience-ideal-completion-date-im-unsure');
      if (unsureBtn) {
        e.preventDefault();
        const question = unsureBtn.closest('.question');
        if (question) {
          const dateInput = question.querySelector('input[type="date"]');
          if (dateInput) dateInput.value = '';
        }
        this.handleContinue(true);
      }
    });

    // Prevent form submission via Enter key except on last question
    this.form.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();

        const visibleQuestions = this.getVisibleQuestions();
        if (this.currentQuestionIndex === visibleQuestions.length - 1) {
          this.handleSubmit();
        } else {
          this.handleContinue();
        }
      }
    });

    // Prevent original form submission
    this.form.addEventListener('submit', (e) => {
      // Check if submission is from our button
      if (!this.form.dataset.stepperAllowSubmit) {
        e.preventDefault();
        return false;
      }
    });
  }
}

// Initialize after jQuery and all conditional logic has completed
function initializeQuestionnaireStepper() {
  // Reset initialization flag if stepper was previously initialized
  // This allows re-initialization when navigating via Turbo/Turbolinks
  if (window.questionnaireStepperInitialized) {
    // Clean up previous instance if it exists
    if (window.questionnaireStepper) {
      // Reset form flag to allow re-initialization
      const form = document.querySelector('.onboarding-form')?.closest('form');
      if (form && form.dataset.stepperInitialized === 'true') {
        form.dataset.stepperInitialized = 'false';
      }
    }
    window.questionnaireStepperInitialized = false;
    window.questionnaireStepper = null;
  }

  // Check if we should use the new stepper (default: yes, unless ENV says 'old')
  const questionnaireMode = window.QUESTIONNAIRE_MODE || 'new';
  const useOldImplementation = questionnaireMode === 'old';

  if (!useOldImplementation) {
    // Wait for jQuery's ready and conditional logic to complete
    // Reduced delay for faster initial load
    setTimeout(() => {
      const stepper = new QuestionnaireStepperController();
      const initialized = stepper.initialize();

      if (initialized) {
        console.log('Questionnaire stepper initialized successfully');
        // Store reference globally for debugging
        window.questionnaireStepper = stepper;
        window.questionnaireStepperInitialized = true;
      }
    }, 300);
  } else {
    console.log('Using old questionnaire implementation');
  }
}

// Expose a global function that jQuery can call after conditional logic changes
window.refreshQuestionnaireStepper = function() {
  if (window.questionnaireStepper && window.questionnaireStepper.refreshVisibilityState) {
    // Minimal wait for jQuery animations to complete
    setTimeout(() => {
      window.questionnaireStepper.refreshVisibilityState();
      window.questionnaireStepper.enforceStepperVisibility();
      window.questionnaireStepper.updateNavigation();
    }, 100); // Reduced from 250ms to 100ms for faster UX
  }
};

// Function to initialize when page is ready
function setupQuestionnaireStepper() {
  // Check if we're on a questionnaire page
  const form = document.querySelector('.onboarding-form')?.closest('form');
  if (!form) {
    return; // Not on a questionnaire page, skip initialization
  }

  // Wait for both DOM and jQuery to be ready
  if (typeof jQuery !== 'undefined') {
    // jQuery is available, use its ready function
    if (jQuery(document).ready) {
      jQuery(document).ready(function() {
        initializeQuestionnaireStepper();
      });
    } else {
      // jQuery might not be fully loaded yet, wait a bit
      setTimeout(() => {
        if (typeof jQuery !== 'undefined' && jQuery(document).ready) {
          jQuery(document).ready(function() {
            initializeQuestionnaireStepper();
          });
        } else {
          initializeQuestionnaireStepper();
        }
      }, 100);
    }
  } else {
    // Fallback to DOMContentLoaded if jQuery isn't available
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeQuestionnaireStepper);
    } else {
      // DOM is already ready
      initializeQuestionnaireStepper();
    }
  }
}

// Initialize on page load
setupQuestionnaireStepper();

// Handle Turbo navigation (Hotwire/Turbo Rails)
document.addEventListener('turbo:load', setupQuestionnaireStepper);
document.addEventListener('turbo:frame-load', function(event) {
  // Only initialize if the frame contains the questionnaire form
  if (event.target.querySelector && event.target.querySelector('.onboarding-form')) {
    setupQuestionnaireStepper();
  }
});

// Handle Turbolinks navigation (legacy)
document.addEventListener('turbolinks:load', setupQuestionnaireStepper);

// Also handle when page becomes visible (for browser back/forward)
document.addEventListener('visibilitychange', function() {
  if (!document.hidden) {
    // Page became visible, check if we need to initialize
    const form = document.querySelector('.onboarding-form')?.closest('form');
    if (form && !window.questionnaireStepperInitialized) {
      setupQuestionnaireStepper();
    }
  }
});
